import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Check from '@mui/icons-material/Check';
import { Layers } from 'lucide-react';
import Responsive from 'src/components/responsive';
import { useMobile } from 'src/theme/responsive';
import { analyticsClient } from 'src/services/analytics';
import { AnalyticsEvent } from 'src/services/analytics/events';

export enum GroupByOption {
    FRESHNESS = 'freshness',
    PROPERTY = 'property',
}

const GROUP_BY_LABELS: Record<GroupByOption, string> = {
    [GroupByOption.FRESHNESS]: 'Freshness',
    [GroupByOption.PROPERTY]: 'Property',
};

type Props = {
    value: GroupByOption;
    onChange: (value: GroupByOption) => void;
};

export default function GroupBySelector({ value, onChange }: Props) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const isMobile = useMobile();

    useEffect(() => {
        setAnchorEl(null);
    }, [isMobile]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    return (
        <>
            <Responsive mobile>
                <IconButton
                    onClick={handleClick}
                    data-testid="group-by-button"
                >
                    <Layers size={20} />
                </IconButton>
            </Responsive>
            <Responsive desktop>
                <Button
                    color="dark"
                    startIcon={<Layers size={18} />}
                    onClick={handleClick}
                    sx={{ whiteSpace: 'nowrap' }}
                    data-testid="group-by-button"
                >
                    <Typography mt={0.25} variant="body2">Group By</Typography>
                </Button>
            </Responsive>

            <Menu
                anchorEl={anchorEl}
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                slotProps={{
                    paper: { sx: { width: 200 } },
                }}
            >
                {Object.values(GroupByOption).map((option) => (
                    <MenuItem
                        key={option}
                        selected={value === option}
                        onClick={() => {
                            if (option === GroupByOption.PROPERTY) {
                                analyticsClient.track(AnalyticsEvent.CONVERSATIONS_GROUP_BY_PROPERTY_SELECTED);
                            }
                            onChange(option);
                            setAnchorEl(null);
                        }}
                        data-testid={`group-by-option-${option}`}
                    >
                        <ListItemIcon sx={{ visibility: value === option ? 'visible' : 'hidden' }}>
                            <Check fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{GROUP_BY_LABELS[option]}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
